import PeopleIcon from '@mui/icons-material/People';
import GroupsIcon from '@mui/icons-material/Groups';
import BusinessIcon from '@mui/icons-material/Business';
import TuneIcon from '@mui/icons-material/Tune';
import ExtractIcon from '@mui/icons-material/TextSnippet';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import StorageIcon from '@mui/icons-material/Storage';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ChatIcon from '@mui/icons-material/Chat';
// Dashboard deaktiviert - Chat ist Startseite
// import DashboardIcon from '@mui/icons-material/Dashboard';
import DescriptionIcon from '@mui/icons-material/Description';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import SettingsIcon from '@mui/icons-material/Settings';
import SourceIcon from '@mui/icons-material/Source';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import LogoutIcon from '@mui/icons-material/Logout';
import HelpOutlineIcon from '@mui/icons-material/HelpOutlineOutlined';
import {
  List, ListItem, ListItemButton, ListItemIcon, ListItemText, Divider,
  ListSubheader, Collapse, Box, Typography, Skeleton, Badge
} from '@mui/material';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '../../hooks/permissions/usePermissions';
import { useFAQModerationCount } from '../../hooks/api/useFAQs';
import { usePendingGroupRequestsCount } from '../../hooks/api/useGroupRequests';

interface User {
  role?: string;
}

interface MenuItem {
  text: string;
  icon: React.ReactNode;
  path: string;
  /** Open items shown as a badge on the icon (for example FAQs awaiting review). */
  badgeCount?: number;
}

interface NavigationMenuProps {
  user: User | null;
  currentPath: string;
  onNavigate: (path: string) => void;
  onLogout: () => void;
  adminSectionOpen: boolean;
  superadminSectionOpen: boolean;
  onAdminSectionToggle: () => void;
  onSuperadminSectionToggle: () => void;
}

export const NavigationMenu: React.FC<NavigationMenuProps> = ({
  user,
  currentPath,
  onNavigate,
  onLogout,
  adminSectionOpen,
  superadminSectionOpen,
  onAdminSectionToggle,
  onSuperadminSectionToggle,
}) => {
  const { t } = useTranslation();
  const { can, hasPermission, isLoading, permissions } = usePermissions();
  const faqModerationCount = useFAQModerationCount(Boolean(can.manageUsers));
  const pendingGroupRequests = usePendingGroupRequestsCount(Boolean(can.manageUsers));

  // Memoized user menu items - available to all users
  const userMenuItems = useMemo((): MenuItem[] => [
    // Dashboard deaktiviert - Chat ist Startseite
    // { text: t('navigation.dashboard'), icon: <DashboardIcon />, path: '/dashboard' },
    { text: t('navigation.chat'), icon: <ChatIcon />, path: '/chat' },
    { text: t('navigation.documents'), icon: <DescriptionIcon />, path: '/documents' },
    // Shared folders hidden during the Moravia pilot (feature stays reachable by URL).
    { text: t('navigation.faqs'), icon: <HelpOutlineIcon />, path: '/faqs', badgeCount: faqModerationCount },
    { text: t('navigation.settings'), icon: <SettingsIcon />, path: '/settings' },
  ], [t, faqModerationCount]);

  // Memoized moderator menu items - for users with moderator permissions
  // Users: requires 'users' permission (moderator+)
  // Groups: requires 'groups.is_visible' permission (standard users can view groups too)
  const moderatorMenuItems = useMemo(() => {
    const items: MenuItem[] = [];
    if (can.manageUsers) {
      items.push({ text: t('navigation.userManagement.users'), icon: <PeopleIcon />, path: '/users' });
    }
    if (permissions?.groups?.is_visible) {
      items.push({
        text: t('navigation.userManagement.groups'),
        icon: <GroupsIcon />,
        path: '/groups',
        badgeCount: pendingGroupRequests,
      });
    }
    return items;
  }, [can.manageUsers, permissions?.groups?.is_visible, pendingGroupRequests, t]);

  // Memoized admin menu items - for users with admin permissions (organization-level configs)
  const adminMenuItems = useMemo(() => {
    if (!can.configureChatModels) {return [];}

    return [
      { text: t('navigation.admin.chatModels'), icon: <SmartToyIcon />, path: '/chat-config' },
      { text: t('navigation.admin.embedding'), icon: <TuneIcon />, path: '/embedding-config' },
      { text: t('navigation.admin.textExtraction'), icon: <ExtractIcon />, path: '/text-extraction-config' },
      { text: t('navigation.admin.documentSources'), icon: <SourceIcon />, path: '/document-source-config' },
      ...(can.configureSMTP ? [{ text: t('navigation.admin.system'), icon: <BusinessIcon />, path: '/system-management' }] : []),
    ];
  }, [can.configureChatModels, can.configureSMTP, t]);

  // Memoized superadmin-only menu items (system-wide configs)
  const superAdminMenuItems = useMemo(() => {
    // Check for data_management permission (superadmin only)
    if (!hasPermission('data_management' as any)) {return [];}

    return [
      { text: t('navigation.superadmin.organizations'), icon: <BusinessIcon />, path: '/superadmin/organizations' },
      { text: t('navigation.superadmin.dataManagement'), icon: <StorageIcon />, path: '/data-management' },
    ];
  }, [hasPermission, t]);

  // Memoized style function for menu items
  const getItemStyles = useCallback((path: string, colorTheme = 'primary') => ({
    '&.Mui-selected': {
      backgroundColor: colorTheme === 'primary' ? 'rgba(0, 48, 100, 0.1)' :
                       colorTheme === 'secondary' ? 'rgba(225, 158, 25, 0.1)' :
                       colorTheme === 'superadmin' ? 'rgba(255, 87, 34, 0.1)' :
                       'rgba(156, 39, 176, 0.1)',
      '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
        color: colorTheme === 'primary' ? 'primary.main' :
               colorTheme === 'secondary' ? 'secondary.main' :
               colorTheme === 'superadmin' ? '#FF5722' :
               '#9C27B0',
      },
    },
    '&:hover': {
      backgroundColor: colorTheme === 'primary' ? 'rgba(0, 48, 100, 0.05)' :
                       colorTheme === 'secondary' ? 'rgba(225, 158, 25, 0.05)' :
                       colorTheme === 'superadmin' ? 'rgba(255, 87, 34, 0.05)' :
                       'rgba(156, 39, 176, 0.05)',
    },
    '& .MuiListItemText-primary': {
      fontFamily: '"Roboto", "Segoe UI", "Helvetica Neue", Arial, sans-serif',
      ...(colorTheme === 'superadmin' && { fontWeight: 600 }),
    },
  }), []);

  // Show loading skeleton while permissions are loading
  // IMPORTANT: This must come AFTER all hooks to comply with Rules of Hooks
  if (isLoading) {
    return (
      <List>
        {[1, 2, 3, 4].map((i) => (
          <ListItem key={i}>
            <Skeleton variant="rectangular" width="100%" height={40} />
          </ListItem>
        ))}
      </List>
    );
  }

  return (
    <>
      {/* User menu items */}
      <List>
        {userMenuItems.map((item) => (
          <ListItem
            key={item.text}
            disablePadding
          >
            <ListItemButton
              onClick={() => onNavigate(item.path)}
              selected={currentPath === item.path || currentPath.startsWith(`${item.path}/`)}
              sx={getItemStyles(item.path, 'primary')}
            >
              <ListItemIcon>
                {item.badgeCount ? (
                  <Badge badgeContent={item.badgeCount} color="warning" max={99}>
                    {item.icon}
                  </Badge>
                ) : (
                  item.icon
                )}
              </ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      
      {/* Moderator section */}
      {moderatorMenuItems.length > 0 && (
        <>
          <Divider />
          <List>
            <ListSubheader sx={{ backgroundColor: 'background.paper' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <SupervisorAccountIcon sx={{ mr: 1, fontSize: 18, color: 'text.secondary' }} />
                <Typography variant="subtitle2" color="text.secondary">
                  {t('navigation.userManagement.title')}
                </Typography>
              </Box>
            </ListSubheader>
            {moderatorMenuItems.map((item) => (
              <ListItem
                key={item.text}
                disablePadding
              >
                <ListItemButton
                  onClick={() => onNavigate(item.path)}
                  selected={currentPath === item.path}
                  sx={{
                    pl: 3,
                    ...getItemStyles(item.path, 'moderator')
                  }}
                >
                  <ListItemIcon>
                    {item.badgeCount ? (
                      <Badge badgeContent={item.badgeCount} color="warning" max={99}>
                        {item.icon}
                      </Badge>
                    ) : (
                      item.icon
                    )}
                  </ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </>
      )}
      
      {/* Admin section - collapsible */}
      {adminMenuItems.length > 0 && (
        <>
          <Divider />
          <List>
            <ListItem disablePadding>
              <ListItemButton
                onClick={onAdminSectionToggle}
                sx={{
                  backgroundColor: 'rgba(225, 158, 25, 0.05)',
                  '&:hover': {
                    backgroundColor: 'rgba(225, 158, 25, 0.1)',
                  },
                }}
              >
                <ListItemIcon>
                  <AdminPanelSettingsIcon sx={{ color: 'secondary.main' }} />
                </ListItemIcon>
                <ListItemText
                  primary={t('navigation.admin.title')}
                  slotProps={{
                    primary: {
                      sx: {
                        fontWeight: 600,
                        color: 'secondary.main'
                      }
                    }
                  }}
                />
                {adminSectionOpen ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
            </ListItem>
            <Collapse in={adminSectionOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {adminMenuItems.map((item) => (
                  <ListItem
                    key={item.text}
                    disablePadding
                  >
                    <ListItemButton
                      onClick={() => onNavigate(item.path)}
                      selected={currentPath === item.path}
                      sx={{
                        pl: 4,
                        ...getItemStyles(item.path, 'secondary')
                      }}
                    >
                      <ListItemIcon>{item.icon}</ListItemIcon>
                      <ListItemText primary={item.text} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Collapse>
          </List>
        </>
      )}
      
      {/* Superadmin section - collapsible */}
      {superAdminMenuItems.length > 0 && (
        <>
          <Divider />
          <List>
            <ListItem disablePadding>
              <ListItemButton
                onClick={onSuperadminSectionToggle}
                sx={{
                  backgroundColor: 'rgba(255, 87, 34, 0.05)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 87, 34, 0.1)',
                  },
                }}
              >
                <ListItemIcon>
                  <SupervisorAccountIcon sx={{ color: '#FF5722' }} />
                </ListItemIcon>
                <ListItemText
                  primary={t('navigation.superadmin.title')}
                  slotProps={{
                    primary: {
                      sx: {
                        fontWeight: 700,
                        color: '#FF5722'
                      }
                    }
                  }}
                />
                {superadminSectionOpen ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
            </ListItem>
            <Collapse in={superadminSectionOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {superAdminMenuItems.map((item) => (
                  <ListItem
                    key={item.text}
                    disablePadding
                  >
                    <ListItemButton
                      onClick={() => onNavigate(item.path)}
                      selected={currentPath === item.path}
                      sx={{
                        pl: 4,
                        ...getItemStyles(item.path, 'superadmin')
                      }}
                    >
                      <ListItemIcon>{item.icon}</ListItemIcon>
                      <ListItemText primary={item.text} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Collapse>
          </List>
        </>
      )}

      <Divider />
      <List>
        <ListItem disablePadding>
          <ListItemButton
            onClick={onLogout}
            sx={{
              '&:hover': {
                backgroundColor: 'rgba(225, 158, 25, 0.1)',
              },
              '& .MuiListItemIcon-root': {
                color: 'secondary.main',
              },
              '& .MuiListItemText-primary': {
                fontFamily: '"Roboto", "Segoe UI", "Helvetica Neue", Arial, sans-serif',
              },
            }}
          >
            <ListItemIcon><LogoutIcon /></ListItemIcon>
            <ListItemText primary={t('user.logout')} />
          </ListItemButton>
        </ListItem>
      </List>
    </>
  );
};